// src/views/settings/SettingsHome.jsx
import React from "react";
import { Link } from "react-router-dom";
import { HeaderPortal } from "../../components/ui-components/HeaderSlot.jsx";
import { useDetectedSourcesCount } from "../../hooks/useDetectedSourcesCount";
import { useCapabilities } from "../../authz/useCapabilities";
import { CAPS } from "../../authz/capabilities";

// Settings landing. For now a simple hub: a detected-source triage banner plus
// category quick-links. This is the seam where the guided setup checklist lands later.
//
// Each link's `show` gate mirrors SettingsSidebar's `can(CAP)` checks (see
// src/SettingsSidebar.js) so a card is never rendered as a dead-end for a
// role that can't reach the destination page/API.
const CATEGORIES = [
  {
    label: "Financial model",
    links: [
      { title: "Cost Sources", to: "/allocation", desc: "Define and own the cost sources feeding allocation" },
      { title: "Allocation Hierarchy", to: "/cost-allocation/hierarchy", desc: "How costs roll up to owners" },
      { title: "Taxonomies", to: "/cost-source-taxonomy", desc: "Cost source categorization", show: (can) => can(CAPS.CONFIG_DIMENSIONS) },
    ],
  },
  {
    label: "Data & integrations",
    links: [
      { title: "Data Connections", to: "/source-connections", desc: "Credentials and import health", show: (can) => can(CAPS.INTEGRATIONS_MANAGE) },
      { title: "Data Pipeline", to: "/data-pipeline", desc: "Pipeline, reporting jobs and activity log", show: (can) => can(CAPS.INTEGRATIONS_MANAGE) },
      { title: "Invoice Ingestion", to: "/invoice-inbound-tokens", desc: "Inbound tokens for automated ingestion", show: (can) => can(CAPS.INTEGRATIONS_MANAGE) },
      { title: "AI Subscription Plan", to: "/settings/ai-subscription", desc: "Flat-fee plan used to re-base AI cost", show: (can) => can(CAPS.CONFIG_ALLOC_MODELS) },
    ],
  },
  {
    label: "Access & system",
    links: [
      { title: "Users & Access", to: "/settings/users", desc: "Users, roles and ingest tokens", show: (can) => can(CAPS.USERS_MANAGE) || can(CAPS.ROLES_ASSIGN) },
      { title: "Roles & Access", to: "/settings/roles", desc: "Role catalog: capabilities, risk and conflicts", show: (can) => can(CAPS.ROLES_ASSIGN) },
      { title: "Service accounts", to: "/settings/service-accounts", desc: "Machine-to-machine principals with a restricted capability set", show: (can) => can(CAPS.SERVICE_ACCOUNTS_MANAGE) },
      { title: "Access review", to: "/settings/access-review", desc: "Periodic review of tenant-owner access", show: (can) => can(CAPS.ACCESS_REVIEW) },
      { title: "Audit & evidence", to: "/settings/audit-evidence", desc: "Audit log + effective-access evidence export", show: (can) => can(CAPS.AUDIT_EXPORT) },
      { title: "System Configuration", to: "/provider-mappings", desc: "Provider mappings and parameters", show: (can) => can(CAPS.CONFIG_DIMENSIONS) || can(CAPS.TENANT_CONFIGURE) },
    ],
  },
];

export default function SettingsHome() {
  const detectedCount = useDetectedSourcesCount();
  const { capabilities } = useCapabilities();
  const can = (c) => capabilities.includes(c);
  const visibleCategories = CATEGORIES
    .map((cat) => ({ ...cat, links: cat.links.filter((l) => !l.show || l.show(can)) }))
    .filter((cat) => cat.links.length > 0);

  return (
    <div className="p-6 space-y-6 text-left">
      <HeaderPortal slot="title">
        <h1 className="text-[20px] leading-[1.4] font-extrabold tracking-[-0.015em] text-text font-['Plus_Jakarta_Sans',sans-serif]">
          Settings
        </h1>
      </HeaderPortal>

      {detectedCount > 0 && (
        <Link
          to="/detected-sources"
          className="block rounded-card border border-warning bg-warning-soft px-4 py-3 text-sm text-text no-underline hover:opacity-90"
        >
          <span className="font-semibold">{detectedCount} cost source{detectedCount === 1 ? "" : "s"} detected</span>{" "}
          awaiting an owner — review and assign →
        </Link>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {visibleCategories.map((cat) => (
          <div key={cat.label} className="rounded-card border border-card-bd bg-card p-5">
            <div className="text-[10.5px] font-bold tracking-[0.08em] uppercase text-text-3 mb-3">{cat.label}</div>
            <div className="space-y-3">
              {cat.links.map((l) => (
                <Link key={l.to} to={l.to} className="block no-underline group">
                  <div className="text-sm font-semibold text-text group-hover:text-primary">{l.title}</div>
                  <div className="text-xs text-text-3">{l.desc}</div>
                </Link>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
